import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Can } from '../../../lib/session';
import { formatQuantity } from '../../../lib/format';
import { links } from '../../../lib/links';
import { WidgetCard, WidgetEmpty, WidgetError, WidgetList, WidgetSkeleton } from '../primitives';
import { lowStockQuery } from '../queries';

/**
 * What needs buying or making. Each row can start a receipt for that material
 * directly, because "this is low" and "receive some" are the same thought.
 */
export function LowStockWidget({
  limit = 5,
  to,
  title = 'Low stock',
}: {
  limit?: number;
  to?: string;
  title?: string;
}) {
  const query = useQuery(lowStockQuery);
  const rows = (query.data?.items ?? []).slice(0, limit);

  return (
    <WidgetCard
      title={title}
      description="At or below the reorder point."
      to={to}
      toLabel="Open inventory"
    >
      {query.isPending ? <WidgetSkeleton /> : null}
      {query.isError ? (
        <WidgetError label="Could not load low stock." onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && query.data.items.length === 0 ? (
        <WidgetEmpty>Everything is above its reorder point.</WidgetEmpty>
      ) : null}
      {rows.length > 0 ? (
        <WidgetList>
          {rows.map((row) => (
            <li key={row.itemId} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{row.name}</p>
                <p className="truncate text-xs text-ink-muted">
                  <span className="font-mono">{row.code}</span> · on hand{' '}
                  {formatQuantity(row.onHand, row.baseUom)}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="whitespace-nowrap text-sm font-semibold text-red-700">
                  short {formatQuantity(row.shortfall, row.baseUom)}
                </p>
                <Can capability="inventory.manage">
                  <Link
                    to={links.inventoryReceive(row.itemId)}
                    className="text-xs font-bold uppercase tracking-wide text-brand-deep hover:underline"
                  >
                    Receive
                  </Link>
                </Can>
              </div>
            </li>
          ))}
        </WidgetList>
      ) : null}
    </WidgetCard>
  );
}
